/*****************************************************************************************
 * 설명 : 프로젝트관리 - 파일관리
 * URI : /project
 * 작성자 :
 * 작성일 :
*****************************************************************************************/
import FolderIcon from '@mui/icons-material/Folder';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Modal from '@mui/material/Modal';
import AgGridReact from 'components/agGridReactEx';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SplitPane from 'react-split-pane';
import * as Yup from "yup";

import { baseURL, DOWNLOAD_FILE, MESSAGE_DELAY } from 'config/config';
import useGridConfig from 'hooks/useGridConfig';

import AlertDialog from 'components/alertDiolog';
import BtnAgGridSave from 'components/btnAgGridSave';
import ChipEx from 'components/chipEx';
import { BtnRefresh, BtnSearch, CurrencyFormat, CustomPagenation } from 'components/common';
import { ButtonEx, InputEx, SelectEx, ToggleButtonGroupEx } from 'components/inputEx';
import alertMsg from 'components/message';
import customTooltipAgGrid from 'components/tooltipAgGrid';

import { getCommonListApi } from 'service/common';
import {
  deleteFileListApi, getPathInfoApi,
  getProjectBatchApi, getProjectPathDirectoryApi,
  getProjectPathDirectoryFileApi,
  setProjectFileComplete,
  setProjectFileTagApi, setProjectPathScanBackground,
  setProjectThumbBackground
} from 'service/project';
import Restful from "service/restful";
import { comma, formatBytes, getSelectData, isCheckValue, PaperComponent, selectGrid } from 'service/utils';

import ExcelModal from 'pages/admin/project/modal/excelModal';
import FileModal from 'pages/admin/project/modal/fileModal';
import ProjectBatchErrorModal from '../project/modal/batchErrorModal';
import DirectoryScanModal from '../project/modal/directoryScanModal';

/*****************************************************************************************
 * 설명 : 함수 선언
*****************************************************************************************/
const normalizeDirPath = (path) => String(path || '').replace(/\\/g, '/').replace(/\/+$/, '');

const getDirectoryLevel = (item, rootPath) => {
  if( item?.fileName === '' || item?.title === 'ROOT' ) return 0;

  const path = normalizeDirPath(item?.path);
  const root = normalizeDirPath(rootPath);
  let relative = path;

  if( root && (path === root || path.startsWith(root + '/')) ) {
    relative = path.slice(root.length);
  }

  relative = relative.replace(/^\/+/, '');
  if( ! relative ) return 0;

  return relative.split('/').filter(Boolean).length;
};

const withDirectoryLevels = (list) => {
  if( ! list?.length ) return [];
  const rootPath = list[0]?.path || '';
  return list.map((item) => ({
    ...item,
    level: getDirectoryLevel(item, rootPath)
  }));
};

const TabDesignFile = ( props ) => {

  /***************************************************************************************
   * 설명 : 변수 선언부
  ***************************************************************************************/
  const [getApi] = Restful();

  const user = useSelector(state => state?.userInfo?.user);

  const [gridApi, setGridApi] = useState({});
  const [gridApiDir, setGridApiDir] = useState({});
  const [gridApiFile, setGridApiFile] = useState({});

  const [batchList, setBatchList] = useState([]);
  const [directoryList, setDirectoryList] = useState([]);
  const [directoryListLoading, setDirectoryListLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [fileListLoading, setFileListLoading] = useState(false);
  const [fileListError, setFileListError] = useState(false);

  const [isUploadCheck, setIsUploadCheck] = useState('');

  const [selected, setSelected] = useState({});
  const [pathInfo, setPathInfo] = useState({
    totalDirectoryCount: 0,
    totalFileCount: 0,
    totalSize: 0
  });

  const [dirPathInfo, setDirPathInfo] = useState({
    totalDirectoryCount: 0,
    totalFileCount: 0,
    totalSize: 0
  });

  const [openModal, setOpenModal] = useState({open: false, modal: 'sync', data: []});

  const initialValues = {
    ext: '',
    extSearch: '',
    fileSize: '',
    sizeType: 1,
    searchText: '',
    pageNo: 1,
    pageRow: 100,
    totalPage: 1,
    totalCount: 0,
  }

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object().shape({
      searchText: Yup.string().max(30, "30자리"),
      fileSize: Yup.number().typeError("숫자"),
    }),
    onSubmit: (values) => {
      formik.setFieldValue('pageNo', 1);
      getFileList(1);
    }
  });

  const [selectedNode, setSelectedNode] = useState({});
  const directoryReadyRef = useRef(false);
  const [fileExtList, setFileExtList] = useState([]);

  const [columnDefs, setColumnDefs] = useState([
    { headerName: '', field: 'seq', width: 35, cellClass: 'center', flex: 0,
      headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true },
    { headerName: '순번', field: 'rowIndex', width: 45, cellClass: 'cp text-right',
      valueGetter: (params) => {
        return comma(params.node.rowIndex + 1);
      }
    },
    { headerName: '상태', field: 'status', width: 90, cellClass: "cp center",
      cellRendererFramework: function(params) {
        if( parseInt(params.data.status) === 0 )
          return <ChipEx color="secondary" variant="outlined" size="small" label="일괄등록중" />;
        else if( parseInt(params.data.status) === 1 )
          return <ChipEx color="error" variant="outlined" size="small" label="등록에러" />;
        else
          return <ChipEx color="success" variant="outlined" size="small" label="등록완료" />;
      }
    },
    { headerName: '등록구분', field: 'type', width: 90, cellClass: "cp center",
      cellRendererFramework: function(params) {
        if( parseInt(params.data.type) === 0 )
          return <ChipEx color="primary" variant="outlined" size="small" label="개별등록" />;
        else if( parseInt(params.data.type) === 1 )
          return <ChipEx color="success" variant="outlined" size="small" label="일괄등록" />;
        else if( parseInt(params.data.type) === 3 )
          return <ChipEx color="secondary" variant="outlined" size="small" label="섬네일생성" />;
        else if( parseInt(params.data.type) === 4 )
          return <ChipEx color="error" variant="outlined" size="small" label="파일삭제" />;
        else
          return <ChipEx color="error" variant="outlined" size="small" label="파일없음" />;
      }
    },
    { headerName: '등록일시', field: 'createDate', width: 140, cellClass: "cp center" },
    { headerName: '폴더개수', field: 'directoryCount', width: 80, cellClass: "cp text-right", valueFormatter: CurrencyFormat },
    { headerName: '파일개수', field: 'fileCount', width: 80, cellClass: "cp text-right", valueFormatter: CurrencyFormat },
    { headerName: '용량(GB)', field: 'totalSize', width: 80, cellClass: "cp text-right",
      valueGetter: (params) => {
        if(params.data.totalSize === 0)
          return 0;
        else
          return comma(parseFloat(params.data.totalSize / (1024*1024*1024)).toFixed(2));
      }
    },
    { headerName: '메시지', field: 'message', width: 300, cellClass: "cp", tooltipField: 'message'},
    { headerName: '에러로그', field: 'log', width: 300, cellClass: "cp", tooltipField: 'log' },
  ]);

  // table column
  const [columnDefsFile, setColumnDefsFile] = useState([
    { headerName: '', field: 'seq', width: 40, cellClass: 'center', flex: 0,
      headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true },
    { headerName:'파일명', field: 'title', width: 300, cellClass: 'cp', tooltipField: 'title',
      cellRendererFramework: function (params) {
        if( params.data.type === 1 )
          return params.data.title;
        else
          return (
            <div
              className="flex vcenter" style={{height: '100%'}}>
                <FolderIcon style={{fontSize: '15px'}}/>
                <div className="ml5" style={{width: 'calc(100% - 30px)'}}>
                  {params.data.title}
                </div>
            </div>
          );
      }
    },
    { headerName:'파일', field: 'path', hide: true},
    { headerName:'실제위치', field: 'destPath', hide: true},
    { headerName:'용량', field: 'fileSize', width: 100, cellClass: 'cp text-right',
      valueGetter: (params) => {
        return formatBytes(params.data.fileSize, 2);
      }
    },
    { headerName:'태그', field: 'tag', width: 200, cellClass: 'cp', editable: true, tooltipField: 'tag'},
    { headerName:'등록일시', field: 'createDate', width: 150, cellClass: 'cp center' },
    { headerName:'다운로드', field: 'download', width: 70, cellClass: 'cp center',
      cellRendererFramework: function (params) {
        return (
          <div className={"grid-icon"}>
            <Button
              color="inherit"
              onClick={() => {
                let url = baseURL + '/api/v1/file/download?seq=' + params.data.seq;
                window.open(url)
              }}
            >
              <i className="ri-download-line"></i>
            </Button>
          </div>
        );
      }
    },
    { headerName:'미리보기', field: 'preview', width: 70, cellClass: 'cp center',
      cellRendererFramework: function (params) {
        return (
          <div className={"grid-icon"}>
            { DOWNLOAD_FILE.includes(params.data.ext?.toLowerCase()) &&
              <Button
                color="inherit"
                onClick={() => {
                  let url = 'http://archive.dagroup.co.kr/synap/SynapDocViewServer/job?fileType=Local&sync=true&fid=' + params.data.seq + '&filePath=';
                  window.open(url + encodeURIComponent(params.data.destPath))
                }}
              >
                <i className="ri-eye-line"></i>
              </Button>
            }
            {/*
            <Button color="inherit"><i className="ri-archive-drawer-line"></i></Button>
            <Button color="inherit"><i className="ri-delete-bin-line"></i></Button>
            <Button  color="inherit" onClick={() => toggleMenuView()}><i className=" ri-more-2-line"></i></Button>
            <div style={{display: menuView === 'view' ? 'block' : 'none'}}>
              <ul className="popup-menu" onClick={() => setMenuView('')}>
                <li>열람 권한신청</li>
                <li className="down-line">다운로드 권한신청</li>
                <li>복사하기<span className="hotkey">~^C</span></li>
                <li className="down-line">잘라내기<span className="hotkey">~^X</span></li>
                <li className="down-line">붙여넣기<span className="hotkey">~^V</span></li>
                <li className="down-line">삭제하기<span className="hotkey">~^D</span></li>
                <li>다운로드</li>
                <li>미리보기</li>
              </ul>
            </div>
            */}
          </div>
        );
      }
    },
    { headerName:'연관프로젝트', field: 'relationProject', width: 200, cellClass: 'cp', editable: true, tooltipField: 'relationProject' },
    { headerName:'비고', field: 'memo', width: 200, cellClass: 'cp', editable: true, tooltipField: 'memo' },
  ]);

  const [gridConfig, setGridUpdate, setGridInit22, gridRemountKey] = useGridConfig(22, setColumnDefs);
  const [gridConfigFile, setGridUpdateFile, setGridInit22File, gridRemountKeyFile] = useGridConfig(22, setColumnDefsFile);

  const [columnDefsDir] = useState([
    { headerName: '', colId: 'chk', width: 36, cellClass: 'cp center folder-check', checkboxSelection: true, sortable: false, filter: false, resizable: false, pinned: 'left', valueFormatter: () => '' },
    { headerName: '폴더명', field: 'title', flex: 1, minWidth: 220, cellClass: 'cp', tooltipField: 'title', filter: true, wrapText: true,
      cellRendererFramework: function (params) {
        const level = params.data?.level || 0;
        return (
          <div className="flex vcenter" style={{height: '100%', paddingLeft: level * 14}}>
            <FolderIcon style={{fontSize: '15px', flexShrink: 0}}/>
            <div className="ml5">
              {params.data?.title}
            </div>
          </div>
        );
      }
    },
  ]);

  /***************************************************************************************
   * 설명 : 경로 리스트 가져오기
  ***************************************************************************************/
  const getList = () => {
    setDirectoryList([]);
    setFileList([]);

    let params = {
      projectNumber: props.selected?.projectNumber
    }

    getProjectBatchApi(getApi, params, (response) => {
      if( response !== undefined && response.data.result && response.data.data && response.data.data.length > 0 ) {
        setBatchList(response.data.data);

        const hasProcessing = response.data.data.some((item) => parseInt(item.status) === 0);
        if( ! hasProcessing && isUploadCheck === 0 ) {
          const latestStatus = response.data.data[0]?.status;
          setIsUploadCheck(latestStatus ?? null);

          if( props.setSelected && props.selected ) {
            let tmp = {...props.selected};
            tmp.isUploadCheck = latestStatus ?? null;
            props.setSelected(tmp);

            if( props.setList && props.selectedIndex > -1 ) {
              let tmpList = [...props.list];
              tmpList[props.selectedIndex] = tmp;
              props.setList(tmpList);
            }
          }
        }

      } else {
        setBatchList([]);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 선택한 파일리스트 삭제
  ***************************************************************************************/
  const deleteFileList = () => {
    let nodes = gridApiFile.getSelectedRows();

    if( nodes.length < 1 ) {
      alertMsg("삭제하고자 하는 파일을 선택하시기 바랍니다.", "W", MESSAGE_DELAY);
      return;
    }

    let params = {
      projectNumber: props.selected?.projectNumber,
      data: nodes
    }

    deleteFileListApi(getApi, params, (response) => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        getFileList();

        let params1 = {
          seq: selectedNode.seq || 0,
          projectNumber: props.selected?.projectNumber,
          ...formik.values
        }

        getPathInfoApi(getApi, params1, (response) => {
          if( response !== undefined && response.data.result ) {
            setPathInfo(response.data.data);
            setDirPathInfo(response.data.data1);
          }
        });

      } else {
        alertMsg(response.data.message || '서버와의 통신에 실패하였습니다.', "E", MESSAGE_DELAY);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 그리드에서 태그 직접 수정
  ***************************************************************************************/
  const modifyTag = (data) => {
    let params = {
      seq: data.data?.seq,
      tag: data.data?.tag
    }

    setProjectFileTagApi(getApi, params, (response) => {
      if( response !== undefined && response.data.result ) {

      } else {
        alertMsg(response.data.message || '서버와의 통신에 실패하였습니다.', "E", MESSAGE_DELAY);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 전체 폴더 목록 가져오기
  ***************************************************************************************/
  const getDirectoryList = () => {
    directoryReadyRef.current = false;
    setDirectoryListLoading(true);
    setFileListLoading(true);
    setFileListError(false);
    setSelectedNode({});
    setDirectoryList([]);

    getProjectPathDirectoryApi(getApi, {
      projectNumber: props.selected?.projectNumber
    }, (response) => {
      setDirectoryListLoading(false);
      if( response !== undefined && response.data.result && response.data.data && response.data.data.length > 0 ) {
        setPathInfo(response.data.data1);
        const list = withDirectoryLevels(response.data.data);
        directoryReadyRef.current = true;
        setDirectoryList(list);
        setSelectedNode(list[0]);
      } else {
        directoryReadyRef.current = false;
        setDirectoryList([]);
        setFileList([]);
        setFileListLoading(false);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 디렉토리 선택 시 파일 리스트 가져오기
  ***************************************************************************************/
  const getFileList = (pageNo) => {
    if( ! props.selected?.projectNumber ) return;
    if( ! directoryReadyRef.current ) return;

    const currentPageNo = pageNo || formik.values.pageNo || 1;

    setFileListLoading(true);
    setFileListError(false);

    let params = {
      seq: selectedNode.seq || 0,
      projectNumber: props.selected?.projectNumber,
      ext: formik.values.ext,
      extSearch: formik.values.extSearch,
      fileSize: formik.values.fileSize,
      sizeType: formik.values.sizeType,
      searchText: formik.values.searchText,
      pageNo: currentPageNo,
      pageRow: formik.values.pageRow || 100
    }

    getProjectPathDirectoryFileApi(getApi, params, (response) => {
      setFileListLoading(false);
      if( response !== undefined && response.data.result ) {
        setFileList(response.data.data || []);
        const info = response.data.data1 || { totalDirectoryCount: 0, totalFileCount: 0, totalSize: 0 };
        setDirPathInfo(info);

        const totalCount = info.totalFileCount || 0;
        const pageRow = formik.values.pageRow || 100;
        let totalPage = Math.floor(totalCount / pageRow);
        if( totalCount % pageRow > 0 ) totalPage = totalPage + 1;
        if( totalPage < 1 ) totalPage = 1;
        formik.setFieldValue('totalPage', totalPage);
        formik.setFieldValue('totalCount', totalCount);
      } else {
        setFileList([]);
        formik.setFieldValue('totalPage', 1);
        formik.setFieldValue('totalCount', 0);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 경로 분석 처리
   * 사용안함, 향후 사용을 위해 남겨둠
  ***************************************************************************************
  const pathScan = () => {
    let params = {
      ...props.selected
    }

    setProjectPathScan(getApi, params, (response) => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        if( response.data.data && response.data.data.length > 0 ) {
          //setDirectoryList(response.data.data);
          //setFileList(response.data.data1);
          getDirectoryList();

          // 프로젝트 상태 업데이트
          let tmp = [...props.list];
          tmp[props.selectedIndex].isFile = 1
          props.setSelected(tmp[props.selectedIndex]);
          props.setList(tmp);

          getList();
        }

      } else {
        alertMsg(response.data.message || '서버와의 통신에 실패하였습니다.', "E", MESSAGE_DELAY);
      }
    });

    setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })
  }

  /***************************************************************************************
   * 설명 : 경로 분석 처리
  ***************************************************************************************/
  const pathScanBackground = () => {
    let params = {
      ...props.selected
    }

    setProjectPathScanBackground(getApi, params, (response) => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        let tmp = window.localStorage.getItem('da_scan');
        if( tmp && tmp !== '' ) {
          let tmp1 = JSON.parse(tmp);

          if( Array.isArray(tmp1) ) {
            tmp1.push({
              isCheck: false,
              isRefresh: false,
              seq: response.data.data.seq,
              memNo: user.memNo
            });
            window.localStorage.setItem('da_scan', JSON.stringify(tmp1));
          }

        } else {
          let tmp1 = [];
          tmp1.push({
            isCheck: false,
            isRefresh: false,
            seq: response.data.data.seq,
            memNo: user.memNo
          });

          window.localStorage.setItem('da_scan', JSON.stringify(tmp1));
        }

        // 리스트 업로드 상태 변경
        // props.selected?.isUploadCheck
        let tmp2 = {...props.selected};
        tmp2.isUploadCheck = 0;
        setIsUploadCheck(0);
        props.setSelected(tmp2);

        let tmp3 = [...props.list];
        tmp3[props.selectedIndex].isUploadCheck = 0;
        props.setList(tmp3);

      } else {
        alertMsg(response.data.message || '서버와의 통신에 실패하였습니다.', "E", MESSAGE_DELAY);
      }
    });

    setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })
  }

  /***************************************************************************************
   * 설명 : 섬네일 생성
  ***************************************************************************************/
  const thumbBackground = () => {
    let nodes = gridApiFile.getSelectedRows();
    let params = {
      projectNumber: props.selected.projectNumber,
      projectName: props.selected.projectName,
      userId: user.userId,
      data: []
    }

    if( nodes.length > 0 ) {
      params.data = nodes;
    }

    setProjectThumbBackground(getApi, params, (response) => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        let tmp = window.localStorage.getItem('da_scan');
        if( tmp && tmp !== '' ) {
          let tmp1 = JSON.parse(tmp);

          if( Array.isArray(tmp1) ) {
            tmp1.push({
              isCheck: false,
              isRefresh: false,
              seq: response.data.data.seq,
              projectNumber: props.selected.projectNumber,
              memNo: user.memNo
            });
            window.localStorage.setItem('da_scan', JSON.stringify(tmp1));
          }

        } else {
          let tmp1 = [];
          tmp1.push({
            isCheck: false,
            isRefresh: false,
            seq: response.data.data.seq,
            projectNumber: props.selected.projectNumber,
            memNo: user.memNo
          });

          window.localStorage.setItem('da_scan', JSON.stringify(tmp1));
        }

        // 리스트 업로드 상태 변경
        // props.selected?.isUploadCheck
        let tmp2 = {...props.selected};
        tmp2.isUploadCheck = 0;
        setIsUploadCheck(0);
        props.setSelected(tmp2);

        let tmp3 = [...props.list];
        tmp3[props.selectedIndex].isUploadCheck = 0;
        props.setList(tmp3);

      } else {
        alertMsg(response.data.message || '서버와의 통신에 실패하였습니다.', "E", MESSAGE_DELAY);
      }
    });

    setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })
  }

  /***************************************************************************************
   * 설명 : 등록완료 처리
  ***************************************************************************************/
  const setFileComplte = () => {
    let params = {
      ...props.selected,
      isFile: 2
    }

    setProjectFileComplete(getApi, params, (response) => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        // 프로젝트 상태 업데이트
        let tmp = [...props.list];
        tmp[props.selectedIndex].isFile = 2;
        props.setSelected(tmp[props.selectedIndex]);
        props.setList(tmp);

      } else {
        alertMsg(response.data.message || '서버와의 통신에 실패하였습니다.', "E", MESSAGE_DELAY);
      }
    });

    setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })
  }

  /***************************************************************************************
   * 설명 : 공통코드 리스트 데이터 가져오기
  ***************************************************************************************/
  const getCommonList = () => {
    let params = {
      groupCode: 'EXT'
    }

    getCommonListApi(getApi, params, (response) => {
      if( response !== undefined && response.data.result && response.data.data && response.data.data.length > 0 ) {
        setFileExtList(getSelectData(response.data.data, 'commonName', 'commonCode'));
      } else {
        setFileExtList([]);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 폴더/파일 그리드 로딩 오버레이
  ***************************************************************************************/
  useEffect(() => {
    const show = (api) => {
      if( api && typeof api.showLoadingOverlay === 'function' && !api.isDestroyed?.() ) api.showLoadingOverlay();
    };
    const hide = (api) => {
      if( api && typeof api.hideOverlay === 'function' && !api.isDestroyed?.() ) api.hideOverlay();
    };

    if( directoryListLoading ) show(gridApiDir);
    else hide(gridApiDir);

    if( directoryListLoading || fileListLoading ) show(gridApiFile);
    else hide(gridApiFile);
  }, [directoryListLoading, fileListLoading, gridApiDir, gridApiFile]);

  /***************************************************************************************
   * 설명 : 파일형식 변경 시 자동 검색
  ***************************************************************************************/
  useEffect(() => {
    if( props.tabIndex === 1 && formik.values.ext !== 'ext' && selectedNode.seq !== undefined && directoryReadyRef.current ) {
      formik.setFieldValue('pageNo', 1);
      getFileList(1);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.ext]);

  /***************************************************************************************
   * 설명 : 트리 노드 클릭 시 파일 리스트 표시
  ***************************************************************************************/
  useEffect(() => {
    if( props.tabIndex === 1 && selectedNode.seq !== undefined && directoryReadyRef.current ) {
      formik.setFieldValue('pageNo', 1);
      getFileList(1);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode]);

  /***************************************************************************************
   * 설명 : 일괄등록 리스트 불러올 경우 첫번째 데이터 자동 선택
  ***************************************************************************************/
  useEffect(() => {
    if( props.tabIndex === 1 && batchList.length > 0 ) {
      selectGrid(gridApi, selected, setSelected, true);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchList]);

  /***************************************************************************************
   * 설명 : 일괄등록 진행 중 상태 폴링
  ***************************************************************************************/
  useEffect(() => {
    if( props.tabIndex !== 1 || props.selected?.seq === undefined ) return;

    const hasProcessing = isUploadCheck === 0 || batchList.some((item) => parseInt(item.status) === 0);
    if( ! hasProcessing ) return;

    const timer = setInterval(() => {
      getList();
    }, 10000);

    return () => clearInterval(timer);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tabIndex, props.selected?.seq, isUploadCheck, batchList]);

  /***************************************************************************************
   * 설명 : 프로젝트 선택 시 경로 리스트 가져오기
  ***************************************************************************************/
  useEffect(() => {
    if( props.tabIndex === 1 && props.selected.seq !== undefined ) {
      // 파일 ext 리스트
      getCommonList();

      getList();

      getDirectoryList();

      setIsUploadCheck(props.selected?.isUploadCheck);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selected, props.tabIndex]);

  /***************************************************************************************
   * 설명 : html 선언부
  ***************************************************************************************/
  return (
    <section className="project-file" style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box'}}>
      <header className="admin-cms-search-header" style={{flexShrink: 0, marginTop: '10px', marginBottom: '10px'}}>
        <div className="fl left">
          <BtnRefresh click={(event) => {
            formik.setValues(initialValues);
            formik.handleSubmit();
          }}></BtnRefresh>
          <BtnSearch></BtnSearch>
          <BtnAgGridSave click={setGridUpdate}  init={setGridInit22} />
        </div>

        <div className="fl left">
          <div className="fl ml5">
            { // 프로젝트 추가 정보 상태
              props.selected?.isConfirm === null
              ? <ChipEx color="error" variant="variant" size="small" label="등록" className="f12" />
              : ! isCheckValue(props.selected?.detailSeq) && parseInt(props.selected?.isConfirm) !== 1
              ? <ChipEx color="error" variant="variant" size="small" label="등록" className="f12" />
              : isCheckValue(props.selected?.detailSeq) && parseInt(props.selected?.isConfirm) !== 1
              ? <ChipEx color="success" variant="variant" size="small" label="추가" className="f12" />
              : <ChipEx color="primary" variant="variant" size="small" label="등록완료" className="f12" />
            }

            { // 프로젝트 파일 정보 상태
              props.selected?.isUploadCheck === 0 ? <ChipEx color="info" variant="variant" size="small" className="ml5 f12" label="처리중" />
              : ! isCheckValue(props.selected?.isFile) || parseInt(formik.values.isFile) === 0
              ? <ChipEx color="error" variant="variant" size="small" className="ml5 f12" label="미등록" />
              : parseInt(props.selected?.isFile) === 1
              ? <ChipEx color="secondary" variant="variant" size="small" className="ml5 f12" label="등록중" />
              : parseInt(props.selected?.isFile) === 2
              ? <ChipEx color="success" variant="variant" size="small" className="ml5 f12" label="등록완료" />
              : <ChipEx color="primary" variant="variant" size="small" className="ml5 f12" label="처리완료" />
            }
            <div style={{display: "inline-block", verticalAlign: 'middle'}}>
              <span className="ml5 bold f14">[{props.selected?.projectNumber}]</span>
              <span className="pad-newLine"></span>
              <span className="bold f14">{props.selected?.projectName}</span>
            </div>
          </div>
        </div>

        <div className="fr right">
          <ButtonEx
            title="일괄등록"
            auth="isModify"
            variant="outlined"
            color="primary"
            disabled={(isUploadCheck === 0) ? true : false}
            onClick={() => {
              setOpenModal({
                open: true,
                //modal: 'scanModal',
                modal: 'scan',
                data: {
                  project: props?.selected,
                  info: selected
                }
              })
            }}
          />

          <ButtonEx
            title="섬네일 재생성"
            auth="isModify"
            variant="outlined"
            color="primary"
            className="ml5"
            disabled={(isUploadCheck === 0) ? true : false}
            onClick={() => {
              if( ! isCheckValue(props.selected?.isFile) || parseInt(formik.values.isFile) === 0 ) {
                alertMsg("프로젝트 파일 일괄등록을 먼저 진행해 주세요.", "W", MESSAGE_DELAY);
                return;
              }

              setOpenModal({
                open: true,
                modal: 'thumb',
                data: {
                  project: props?.selected,
                  info: selected
                }
              })
            }}
          />

          <ButtonEx
            title="등록완료"
            auth="isModify"
            variant="outlined"
            color="secondary"
            className="ml5"
            disabled={(isUploadCheck === 0) ? true : false}
            onClick={() => {
              setOpenModal({
                open: true,
                modal: 'complete',
                data: {
                  project: props?.selected,
                  info: selected
                }
              })
            }}
          />
        </div>
        <div className="clearfix"></div>
      </header>

      <div className="ag-theme-balham" style={{height: '200px', flexShrink: 0}}>
        <AgGridReact remountKey={gridRemountKey}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: false,
            lockVisible: true,
            tooltipComponent: customTooltipAgGrid,
          }}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          columnDefs={columnDefs}
          rowData={batchList}
          rowSelection={'single'}
          onGridReady={(event) => {
            gridConfig(event);
            setGridApi(event.api);
          }}
          onSelectionChanged={(event) => {
            let nodes = event.api.getSelectedRows();

            if( nodes.length > 0 ) {
              setSelected(nodes);
            } else {
              setSelected([]);
            }
          }}
          onCellDoubleClicked={(event) => {
            // 에러로그 일 경우 모달 표시
            if( event?.colDef?.field === 'log' && parseInt(event.data.status) === 1 ) {
              setOpenModal({open: true, modal: 'error', data: event.data})
            }
          }}
          overlayNoRowsTemplate = "검색된 내용이 없습니다."
        />
      </div>

      <div style={{flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', marginTop: '10px'}}>
      <SplitPane className="file-dir-split" split="vertical" minSize={220} maxSize={720} defaultSize={380} style={{height: '100%', overflow: 'hidden'}}>
        <section style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
          <header className="admin-cms-search-header mb10" style={{flexShrink: 0, minHeight: 0, paddingTop: 0}}>
            <div className="fl left">
              <h4 className="mt0 mb0" style={{lineHeight: '28px'}}>폴더</h4>
            </div>
            <div className="clearfix"></div>
          </header>

          <section className="ag-theme-balham" style={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
            <AgGridReact
              defaultColDef={{
                sortable: true,
                resizable: true,
                filter: true,
                lockVisible: true,
                tooltipComponent: customTooltipAgGrid,
              }}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              columnDefs={columnDefsDir}
              rowData={directoryList}
              rowSelection={'single'}
              getRowNodeId={(data) => String(data?.seq ?? '')}
              onGridReady={(event) => {
                setGridApiDir(event.api);
                if (directoryListLoading) event.api.showLoadingOverlay();
              }}
              onSelectionChanged={(event) => {
                const rows = event.api.getSelectedRows();
                if( rows.length > 0 ) setSelectedNode(rows[0]);
              }}
              onFirstDataRendered={(event) => {
                const first = event.api.getDisplayedRowAtIndex(0);
                if( first ) first.setSelected(true);
              }}
              overlayLoadingTemplate="폴더를 불러오고 있습니다."
              overlayNoRowsTemplate="폴더가 없습니다."
              suppressNoRowsOverlay={directoryListLoading}
            />
          </section>
        </section>

        <section style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
          <header className="admin-cms-search-header clearfix mb10" style={{flexShrink: 0, minHeight: 0, paddingTop: 0}}>
            <div className="fl">
              <form onSubmit={formik.handleSubmit}>
                <BtnRefresh click={(event) => {
                  formik.setValues(initialValues);
                  formik.handleSubmit();
                }}></BtnRefresh>
                <BtnSearch></BtnSearch>
                <BtnAgGridSave click={setGridUpdateFile} init={setGridInit22File} />

                <SelectEx
                  name="ext"
                  formik={formik}
                  fullWidth={false}
                  style={{width: '150px'}}
                  data={[
                    {label: '파일형식', value: ''}
                  ].concat(fileExtList).concat([{label: '직접입력', value: 'ext'}])}
                  onChange={(event) => {
                    if( event.target.value !== 'ext')
                      formik.setFieldValue('extSearch', '');
                  }}
                />

                {formik.values.ext === 'ext' &&
                  <InputEx
                    name="extSearch"
                    formik={formik}
                    label="확장명 입력"
                    className="basic ml5 mr5"
                    style={{width: '100px'}}
                  />
                }

                <InputEx
                  name="fileSize"
                  formik={formik}
                  label="파일크기(MB)"
                  className="basic ml5 mr5"
                  style={{width: '100px'}}
                />

                <ToggleButtonGroupEx
                  name="sizeType"
                  exclusive={true}
                  formik={formik}
                  className="search-toggle-btn mr5"
                  data={[
                    {label: '보다 큰', value: 1},
                    {label: '보다 작은', value: 2},
                  ]}
                />

                <InputEx
                  name="searchText"
                  formik={formik}
                  fullWidth={false}
                  className="pad-mt5"
                  label="파일명, 태그"
                />
              </form>
            </div>

            <div className={"fr tempFolder pad-mt28-minus"}>
              <div className="basicBtn">
                {/*
                <ButtonEx
                  title="열람신청"
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    setOpenModal({open: true, modal: 'applicationPerusal', data: {siteSeq: formik.values.siteSeq}})
                  }}
                />

                <ButtonEx
                  title="임시폴더일괄처리"
                  color="inherit"
                  className="ml5"
                <ButtonEx
                  title="엑셀업로드"
                  color="inherit"
                  className="ml5"
                  variant="outlined"
                  onClick={() => {
                    setOpenModal({open: true, modal: 'excelUpload', data: {siteSeq: formik.values.siteSeq}})
                  }}
                />
                */}
                <ButtonEx
                  auth="isDelete"
                  title="삭제"
                  color="error"
                  className="ml5"
                  variant="outlined"
                  disabled={parseInt(selected[0]?.isConfirm) === 1 ? true : false}
                  onClick={() => {
                    let nodes = gridApiFile.getSelectedRows();

                    if( nodes.length < 1 ) {
                      alertMsg("삭제할 파일을 선택하시기 바랍니다.", "W", MESSAGE_DELAY);
                      return;
                    }

                    setOpenModal({open: true, modal: 'dialog', data: nodes});
                  }}
                />

                <ButtonEx
                  auth="isWrite"
                  title="추가"
                  color="secondary"
                  className="ml5"
                  variant="outlined"
                  disabled={(isUploadCheck === 0) ? true : false}
                  onClick={() => setOpenModal({open: true, modal: 'fileAdd', data: props.selected})}
                />
              </div>
            </div>
          </header>

          <section className="ag-theme-balham grid" style={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
                <AgGridReact remountKey={gridRemountKeyFile}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    filter: false,
                    lockVisible: true,
                    tooltipComponent: customTooltipAgGrid,
                  }}
                  tooltipShowDelay={0}
                  tooltipHideDelay={2000}
                  onGridReady={(event) => {
                    gridConfigFile(event);
                    setGridApiFile(event.api);
                    if (directoryListLoading || fileListLoading) event.api.showLoadingOverlay();
                  }}
                  rowSelection = {'multiple'}
                  columnDefs={columnDefsFile}
                  rowData={fileList}
                  rowDragManaged={true}
                  rowDragMultiRow={true}
                  animateRows={true}
                  overlayLoadingTemplate="파일정보를 불러오고 있습니다."
                  overlayNoRowsTemplate = "검색된 내용이 없습니다."
                  suppressNoRowsOverlay={directoryListLoading || fileListLoading}
                  onRowClicked={() => {
                    setOpenModal({open: true, modal: 'projectInfo', data: {}})
                  }}
                  onCellEditingStopped={(event) => {
                    modifyTag(event.node);
                  }}
                />
          </section>
          <section className="pagination" style={{flexShrink: 0, marginTop: '10px'}}>
            <div className="fl">
              <CustomPagenation
                currPage={{pageNo: formik.values.pageNo, pageRow: formik.values.pageRow}}
                totalPage={formik.values.totalPage}
                totalCount={formik.values.totalCount}
                change={(event, pageNo) => {
                  formik.setFieldValue("pageNo", pageNo);
                  getFileList(pageNo);
                }}
              />
            </div>
          </section>
        </section>
      </SplitPane>
      </div>

      <footer className="footer-txt" style={{flexShrink: 0, marginTop: '10px'}}>
        <div className="fl">
          프로젝트 전체 : 폴더 {comma(pathInfo?.totalDirectoryCount || 0)}개,
          파일 {comma(pathInfo?.totalFileCount || 0)}개
          / 사용용량 : {formatBytes(pathInfo?.totalSize || 0)}
        </div>

        <div className="fr">
          검색결과 : 파일 {comma(dirPathInfo?.totalFileCount || 0)}개
          / 사용용량 : {formatBytes(dirPathInfo?.totalSize || 0)}
        </div>
      </footer>

      { openModal.open && openModal.modal === 'scan' &&
        <Dialog
          open={openModal}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: {}});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "550px",  // Set your width here
              },
            },
          }}
        >
          <AlertDialog
            open={openModal.open}
            close={() => setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })}
            title="경로 분석"
            message={
              <div>
                <div>해당 프로젝트의 디렉토리 및 파일을 상세 분석 하시겠습니까?</div>
                <div className="mt10">이 작업은 오랜시간이 걸릴 수 있습니다.</div>
              </div>
            }
            confirm="분석시작"
            color="primary"
            onClick={() => pathScanBackground()}
          />
        </Dialog>
      }

      { openModal.open && openModal.modal === 'scanModal' &&
        <Dialog
          open={openModal}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: {}});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "1200px",  // Set your width here
              },
            },
          }}
        >
          <DirectoryScanModal
            open={openModal.open}
            data={openModal.data}
            selectedIndex={props.selectedIndex}
            setSelected={props.setSelected}
            list={props.list}
            setList={props.setList}
            getList={getList}
            getDirectoryList={getDirectoryList}
            getFileList={getFileList}
            close={() => setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })}
          />
        </Dialog>
      }

      { openModal.open && openModal.modal === 'excelUpload' &&
        <Modal
          open={openModal.open}
          onClose={() => setOpenModal({ open: false, modalName: 'excelUpload', data: openModal.data })}
        >
          <ExcelModal
            open={openModal.open}
            close={() => setOpenModal({ open: false, modalName: 'excelUpload', data: openModal.data })}
          />
        </Modal>
      }

      { openModal.open && openModal.modal === 'dialog' &&
        <Modal
          open={openModal.open}
          onClose={() => setOpenModal({ open: false, modalName: 'dialog', data: openModal.data })}
        >
          <AlertDialog
            open={openModal.open}
            close={() => setOpenModal({ open: false, modalName: 'dialog', data: openModal.data })}
            title="파일 삭제"
            message={`선택하신 ${openModal.data.length}개의 "파일"를 삭제하시겠습니까?`}
            confirm="삭제"
            onClick={deleteFileList}
          />
        </Modal>
      }

      { openModal.open && openModal.modal === 'complete' &&
        <Modal
          open={openModal.open}
          onClose={() => setOpenModal({ open: false, modal: openModal.modal, data: openModal.data })}
        >
          <AlertDialog
            open={openModal.open}
            close={() => setOpenModal({ open: false, modal: openModal.modal, data: openModal.data })}
            title="파일 등록완료 처리"
            message={
              <div>
                <div>
                  해당 프로젝트의 파일을 등록완료 처리하시겠습니까?
                </div>
              </div>
            }
            color="primary"
            confirm="등록완료"
            onClick={setFileComplte}
          />
        </Modal>
      }

      { openModal.open && openModal.modal === 'fileAdd' &&
        <Dialog
          open={openModal}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: {}});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "1200px",  // Set your width here
              },
            },
          }}
        >
          <FileModal
            open={openModal.open}
            data={openModal.data}
            selected={props.selected}
            selectedNode={selectedNode}
            fileList={fileList}
            setFileList={setFileList}
            getDirectoryList={getDirectoryList}
            getFileList={getFileList}
            getList={getList}
            close={() => setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })}
          />
        </Dialog>
      }

      { openModal.open && openModal.modal === 'thumb' &&
        <Dialog
          open={openModal}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: {}});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "550px",  // Set your width here
              },
            },
          }}
        >
          <AlertDialog
            open={openModal.open}
            width="550px"
            close={() => setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })}
            title="섬네일 재생성"
            message={
              <div>
                <div>선택된 파일의 섬네일을 재생성 하시겠습니까?</div>
                <div className="mt10">이 작업은 오랜시간이 걸릴 수 있습니다.</div>
              </div>
            }
            confirm="섬네일 재생성"
            color="primary"
            onClick={() => thumbBackground()}
          />
        </Dialog>
      }

      { openModal.open && openModal.modal === 'error' &&
        <Dialog
          open={openModal}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: {}});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "1200px",  // Set your width here
                height: '80vh'
              },
            },
          }}
        >
          <ProjectBatchErrorModal
            open={openModal.open}
            data={openModal.data}
            close={() => setOpenModal({ open: false, modalName: openModal.modal, data: openModal.data })}
          />
        </Dialog>
      }
    </section>
  );
}

/*****************************************************************************************
 * 설명 : default export 선언
*****************************************************************************************/
export default TabDesignFile;